'use client';

import Link from '@components/Link';
import { Accordion } from '@ngg/components';
import { getStockStatusText } from '@ngg/storefront-utils';
import Image from 'next/image';
import { type PropsWithChildren } from 'react';
import type { Dictionary } from 'types/common';
import type { Variant } from 'types/product';

import Icon from './icon';

function LineItemLink({
    slug,
    locale,
    children,
    className,
}: PropsWithChildren<{ slug?: string; locale?: string; className?: string }>) {
    if (slug)
        return (
            <Link href={slug} locale={locale} className={className}>
                {children}
            </Link>
        );
    return <div className={className}>{children}</div>;
}

export default function Included({
    items,
    contentfulLocale,
    dictionary,
    className,
}: {
    items: Variant[];
    contentfulLocale?: string;
    dictionary: Dictionary;
    className?: string;
}) {
    const renderVariantStockStatus = (item: Variant) =>
        getStockStatusText({
            dictionary,
            locale: contentfulLocale,
            stockStatus: item.stockStatus,
            deliveryUnit: item.deliveryUnit,
            nextDeliveryDate: item.nextDeliveryDate,
            deliveryMin: item.deliveryMin?.toString(),
            deliveryMax: item.deliveryMax?.toString(),
        });

    return (
        <Accordion
            IconComponent={Icon}
            WrapperComponent="div"
            title={dictionary.includedInThisPackage}
            classNames={{
                wrapper: className,
                button: 'text-left items-center leading-4 p-4 font-medium rounded-none',
                panel: 'p-4',
            }}
            defaultOpen
            transitionProps={{ appear: false }}>
            <ul className="divide-y border">
                {items.map((item, index) => (
                    <li
                        key={item.id + item.title + item.ProductURL}
                        className="">
                        <LineItemLink
                            slug={item.ProductURL}
                            locale={contentfulLocale}
                            className="grid grid-cols-[auto,1fr]">
                            {item.image && (
                                <div className="relative block min-h-24 w-24 flex-shrink-0 bg-grey-100">
                                    <Image
                                        className="h-full w-full overflow-hidden object-contain p-1 mix-blend-multiply"
                                        src={item.image.src}
                                        alt={item.image.alt}
                                        height={100}
                                        width={100}
                                    />
                                </div>
                            )}
                            <div className="grid grid-flow-row gap-2 px-4 py-3">
                                <div className="flex justify-between text-xs uppercase">
                                    <span>{item.brand}</span>
                                    <span className="underline">
                                        {dictionary.details}
                                    </span>
                                </div>
                                <div className="flex flex-1 flex-col overflow-hidden lg:flex-row lg:justify-between">
                                    <span className="truncate">
                                        {item.title}
                                    </span>
                                    {(items.at(index)?.quantity ?? 0) > 1 && (
                                        <span>
                                            {items.at(index)?.quantity}&nbsp;
                                            {dictionary.pieces}
                                        </span>
                                    )}
                                </div>
                                <span className="block text-xs font-bold uppercase empty:hidden">
                                    {renderVariantStockStatus(item)}
                                </span>
                            </div>
                        </LineItemLink>
                    </li>
                ))}
            </ul>
        </Accordion>
    );
}
